import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import { toast, Bounce } from 'react-toastify';
import { receiveStockApi, getUniformItemsApi } from 'api/services/uniformManagement';
import { useUniformMgmt } from '../../context/uniformMgmtContext';
import Breadcrumbs from 'components/Breadcrumbs';

const schema = Yup.object().shape({
  warehouse: Yup.object().nullable().required('Warehouse is required'),
  uniform_item: Yup.object().nullable().required('Uniform item is required'),
  size: Yup.object().nullable().when('uniform_item', {
    is: (item) => !!item && (item.sizes?.length ?? 0) > 0,
    then: (s) => s.required('Size is required'),
    otherwise: (s) => s.notRequired(),
  }),
  quantity: Yup.number().required('Quantity is required').min(1, 'Must be at least 1'),
  notes: Yup.string()
});

const StockReceiving = () => {
  const navigate = useNavigate();
  const { warehouseOptions, warehousesLoading } = useUniformMgmt();
  const [loading, setLoading] = useState(false);
  const [uniformItems, setUniformItems] = useState([]);
  const [uniformItemsLoading, setUniformItemsLoading] = useState(false);
  const [result, setResult] = useState(null);

  const { control, handleSubmit, formState: { errors }, reset, setError, watch, setValue } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { warehouse: null, uniform_item: null, size: null, quantity: 1, notes: '' }
  });

  const selectedItem = watch('uniform_item');
  const itemSizes = selectedItem?.sizes || [];
  const sizeOptions = itemSizes
    .filter(s => s.status !== false)
    .map(s => ({ label: `${s.name} (${s.code})`, value: s.id }));
  const sizeRequired = sizeOptions.length > 0;

  useEffect(() => {
    setUniformItemsLoading(true);
    getUniformItemsApi({ page_size: 1000 }).then(response => {
      setUniformItems(
        (response.data?.results || [])
          .map(i => ({ label: i.name, value: i.id, sizes: i.sizes || [] }))
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
      );
    }).catch(() => {})
      .finally(() => setUniformItemsLoading(false));
  }, []);

  const onSubmit = (data) => {
    setLoading(true);
    setResult(null);
    receiveStockApi({
      warehouse: data.warehouse.value,
      uniform_item: data.uniform_item.value,
      size: data.size?.value || null,
      quantity: data.quantity,
      notes: data.notes || ''
    }).then(response => {
      setLoading(false);
      toast.success(`Received ${data.quantity} units`, { position: "top-right", autoClose: 3000, transition: Bounce });
      setResult(response.data?.data || null);
      reset();
    }).catch(e => {
      setLoading(false);
      const errData = e.response?.data;
      if (typeof errData === 'string') {
        toast.error(errData, { position: "top-right", autoClose: 3000, transition: Bounce });
        return;
      }
      if (errData && typeof errData === 'object') {
        const topLevel = errData.error || errData.detail || errData.non_field_errors?.[0];
        if (topLevel) {
          toast.error(topLevel, { position: "top-right", autoClose: 3000, transition: Bounce });
        }
        Object.entries(errData).forEach(([k, v]) => {
          if (['error', 'detail', 'non_field_errors', 'status'].includes(k)) return;
          setError(k, { type: 'manual', message: Array.isArray(v) ? v.join(', ') : String(v) });
        });
        if (!topLevel && !Object.keys(errData).some(k => !['error', 'detail', 'non_field_errors', 'status'].includes(k))) {
          toast.error('Failed to receive stock', { position: "top-right", autoClose: 3000, transition: Bounce });
        }
      } else {
        toast.error('Failed to receive stock', { position: "top-right", autoClose: 3000, transition: Bounce });
      }
    });
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs maintitle="Stock Receiving" />
<div className="row">
          <div className="col-md-8">
            <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="input-block mb-3">
                        <label className="col-form-label">Warehouse <span className="text-danger">*</span></label>
                        <Controller name="warehouse" control={control}
                          render={({ field: { value, onChange } }) => (
                            <FilterSelect isClearable onChange={onChange} optionLabel="label" optionValue="value"
                              options={warehouseOptions} value={value} placeholder="Select Warehouse" isLoading={warehousesLoading} />
                          )} />
                        {errors.warehouse && <small className="text-danger">{errors.warehouse.message}</small>}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="input-block mb-3">
                        <label className="col-form-label">Uniform Item <span className="text-danger">*</span></label>
                        <Controller name="uniform_item" control={control}
                          render={({ field: { value, onChange } }) => (
                            <FilterSelect isClearable
                              onChange={(v) => { onChange(v); setValue('size', null); }}
                              optionLabel="label" optionValue="value"
                              options={uniformItems} value={value} placeholder="Select Item" isLoading={uniformItemsLoading} />
                          )} />
                        {errors.uniform_item && <small className="text-danger">{errors.uniform_item.message}</small>}
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="input-block mb-3">
                        <label className="col-form-label">
                          Size {sizeRequired && <span className="text-danger">*</span>}
                          {!selectedItem && <small className="text-muted ms-2">(select an item first)</small>}
                          {selectedItem && !sizeRequired && <small className="text-muted ms-2">(no sizes assigned to this item)</small>}
                        </label>
                        <Controller name="size" control={control}
                          render={({ field: { value, onChange } }) => (
                            <FilterSelect isClearable onChange={onChange} optionLabel="label" optionValue="value"
                              options={sizeOptions} value={value}
                              placeholder={!selectedItem ? "Select an item first" : sizeRequired ? "Select Size" : "No sizes available"}
                              isDisabled={!selectedItem || !sizeRequired}
                              isLoading={false} />
                          )} />
                        {errors.size && <small className="text-danger">{errors.size.message}</small>}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="input-block mb-3">
                        <label className="col-form-label">Quantity <span className="text-danger">*</span></label>
                        <Controller name="quantity" control={control}
                          render={({ field }) => (
                            <input className="form-control" {...field} type="number" min="1" placeholder="Quantity" />
                          )} />
                        {errors.quantity && <small className="text-danger">{errors.quantity.message}</small>}
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-12">
                      <div className="input-block mb-3">
                        <label className="col-form-label">Notes</label>
                        <Controller name="notes" control={control}
                          render={({ field }) => <textarea className="form-control" {...field} rows={2} placeholder="Optional notes" />} />
                      </div>
                    </div>
                  </div>
                  <div className="d-flex justify-content-end mt-2">
                    <Button loading={loading} disabled={loading} className="btn btn-primary" type="submit" label="Receive Stock" />
                  </div>
                </form>
          </div>

          {result && (
            <div className="col-md-4">
              <div className="card border-success">
                <div className="card-header bg-success text-white"><h5 className="mb-0">Stock Updated</h5></div>
                <div className="card-body">
                  <p><strong>Warehouse:</strong> {result.warehouse_name}</p>
                  <p><strong>Item:</strong> {result.uniform_item_name}</p>
                  <p><strong>Size:</strong> {result.size_name ? `${result.size_name} (${result.size_code})` : <span className="text-muted">—</span>}</p>
                  <p><strong>New Quantity:</strong> <span className="fs-4 fw-bold text-success">{result.quantity}</span></p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockReceiving;
